import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { ArrowRight, Target, Utensils, RefreshCw, BarChart3 } from "lucide-react";
import { VoraLogo } from "@/components/brand/VoraLogo";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Defer navigation to after render to avoid
  // "Cannot update a component while rendering a different component" warning
  useEffect(() => {
    if (user) {
      navigate(user.onboarding_completed ? "/dashboard" : "/onboarding", { replace: true });
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  // Forward UTM + ref params to signup
  const forwardParams = () => {
    const params = new URLSearchParams(window.location.search);
    const keep = ["ref", "utm_source", "utm_medium", "utm_campaign"];
    const fwd = new URLSearchParams();
    keep.forEach((k) => { if (params.get(k)) fwd.set(k, params.get(k)); });
    const qs = fwd.toString();
    return `/signup${qs ? `?${qs}` : ""}`;
  };

  const features = [
    {
      icon: Target,
      title: "VORA-Optimized Macros",
      description: "Calories and macros calibrated to your body, goals, and activity level."
    },
    {
      icon: Utensils,
      title: "AI-Built Meal Plans",
      description: "VORA AI generates weekly plans matched to your exact nutritional targets."
    },
    {
      icon: RefreshCw,
      title: "One-Click Swap",
      description: "Swap any meal instantly — VORA AI keeps your macros on track."
    },
    {
      icon: BarChart3,
      title: "Smart Tracking",
      description: "Track your progress with VORA AI insights based on your body and goals."
    }
  ];

  return (
    <div className="min-h-screen vora-landing-bg" style={{ overflowY: "auto", height: "100vh" }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50" style={{ background: "#FFFFFF", borderBottom: "1px solid rgba(17,17,17,0.06)" }}>
        <div className="container-app py-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <VoraLogo variant="icon" size={26} />
            <span className="text-lg font-heading font-bold tracking-tight" style={{ color: "#111111" }}>VORA</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/login")}
              data-testid="login-nav-btn"
              className="text-[#6B7280] hover:text-[#111111]"
            >
              Log in
            </Button>
            <Button 
              onClick={() => navigate(forwardParams())}
              data-testid="signup-nav-btn"
              className="vora-cta-primary rounded-full px-6"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 pb-28 px-4" data-testid="landing-hero-section">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — Copy */}
            <div className="animate-fade-in">
              <span 
                className="inline-block text-[11px] font-semibold tracking-[0.14em] uppercase mb-5"
                style={{ color: "#4E7A67" }}
                data-testid="landing-hero-caption"
              >
                Optimized by VORA AI
              </span>
              <h1 
                className="mb-6" 
                style={{ 
                  color: "#111111", 
                  fontSize: "clamp(2.5rem, 5vw, 3.75rem)", 
                  fontWeight: 800, 
                  lineHeight: 1.05, 
                  letterSpacing: "-0.035em",
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
                data-testid="landing-hero-heading"
              >
                Nutrition that<br />
                <span style={{ color: "#4E7A67" }}>adapts to you</span>
              </h1>
              <p 
                className="mb-10 max-w-md" 
                style={{ color: "#5F6368", fontSize: "1.125rem", lineHeight: 1.6 }} 
                data-testid="landing-hero-sub"
              >
                VORA AI builds personalized meal plans around your body and goals. No counting, no guessing — just results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-5">
                <Button 
                  onClick={() => navigate(forwardParams())}
                  data-testid="hero-get-started-btn"
                  className="vora-cta-primary flex items-center gap-2 rounded-full px-8 py-3.5 text-base"
                >
                  Get Started Free <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/login")}
                  data-testid="hero-login-btn"
                  className="vora-cta-secondary rounded-full px-8 py-3.5 text-base"
                >
                  I have an account
                </Button>
              </div>
              <p className="text-xs" style={{ color: "#8B9096" }} data-testid="landing-hero-trust">
                Free to start — no credit card required
              </p>
            </div>

            {/* Right — Image */}
            <div className="relative animate-fade-in stagger-2 flex justify-center lg:justify-end">
              <div 
                className="relative overflow-hidden w-full max-w-[520px]"
                style={{ borderRadius: "24px", boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1679541946014-a47f1ec0dc1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxtZWRpdGVycmFuZWFuJTIwYXNpYW4lMjBmdXNpb24lMjBoZWFsdGh5JTIwZm9vZCUyMGJvd2wlMjBvdmVyaGVhZHxlbnwwfHx8fDE3NzIzNDA4ODV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Healthy meal bowl"
                  className="w-full aspect-[4/3] object-cover vora-img"
                  data-testid="landing-hero-image"
                />
                {/* Minimal chip */}
                <div 
                  className="absolute bottom-4 left-4 flex items-center gap-2 px-3.5 py-2"
                  style={{ 
                    background: "rgba(255,255,255,0.88)", 
                    backdropFilter: "blur(12px)", 
                    WebkitBackdropFilter: "blur(12px)",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(17,17,17,0.06)"
                  }}
                  data-testid="landing-hero-chip"
                >
                  <div 
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(78,122,103,0.10)" }}
                  >
                    <Target className="w-3.5 h-3.5" style={{ color: "#4E7A67" }} />
                  </div>
                  <span className="text-xs font-semibold" style={{ color: "#111111" }}>2,100 kcal · AI-personalized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 vora-section-c" data-testid="landing-features-section">
        <div className="container-app">
          <div className="text-center mb-14">
            <h2 
              className="mb-4" 
              style={{ color: "#111111", fontSize: "clamp(1.5rem, 3vw, 2.25rem)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 }}
              data-testid="landing-features-heading"
            >
              Everything VORA AI handles for you
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: "#5F6368", lineHeight: 1.6 }}>
              From calibrated targets to smart meal planning — so you can focus on your goals.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className={`animate-fade-in stagger-${index + 1}`}
                style={{ 
                  background: "#FFFFFF", 
                  border: "1px solid rgba(0,0,0,0.03)", 
                  borderRadius: "20px",
                  padding: "28px 24px",
                  transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s", 
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)" 
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 14px 40px rgba(0,0,0,0.07)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.05)"; }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(78,122,103,0.07)" }}>
                  <feature.icon className="w-5 h-5" style={{ color: "#4E7A67" }} />
                </div>
                <h3 className="text-base font-bold mb-1.5" style={{ color: "#111111" }}>{feature.title}</h3>
                <p className="text-sm" style={{ color: "#5F6368", lineHeight: 1.55 }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meal Preview Section */}
      <section className="py-24 vora-section-b" data-testid="landing-meals-section">
        <div className="container-app">
          <div className="text-center mb-14">
            <h2 
              className="mb-4" 
              style={{ color: "#111111", fontSize: "clamp(1.5rem, 3vw, 2.25rem)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 }}
              data-testid="landing-meals-heading"
            >
              Meals optimized by VORA AI
            </h2>
            <p className="text-lg max-w-lg mx-auto" style={{ color: "#5F6368", lineHeight: 1.6 }}>
              Delicious recipes calibrated to your macros and goals.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { name: "Grilled Salmon Bowl", img: "https://images.unsplash.com/photo-1645177628172-a94c1f96e6db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxncmlsbGVkJTIwc2FsbW9uJTIwcXVpbm9hJTIwYm93bHxlbnwwfHx8fDE3NzIzNDA4ODh8MA&ixlib=rb-4.1.0&q=85", macros: "42g protein" },
              { name: "Chicken Stir-Fry", img: "https://images.unsplash.com/photo-1592578630143-fac65cda7a67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxjaGlja2VuJTIwc3RpciUyMGZyeSUyMHZlZ2V0YWJsZXMlMjBoZWFsdGh5fGVufDB8fHx8MTc3MjM0MDg4OXww&ixlib=rb-4.1.0&q=85", macros: "45g protein" },
              { name: "Avocado Toast", img: "https://images.pexels.com/photos/7937462/pexels-photo-7937462.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", macros: "20g protein" },
              { name: "Greek Yogurt Bowl", img: "https://images.unsplash.com/photo-1753173302072-ac0fa1f82368?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxncmVlayUyMHlvZ3VydCUyMGdyYW5vbGElMjBiZXJyaWVzJTIwYm93bHxlbnwwfHx8fDE3NzIzNDA4OTF8MA&ixlib=rb-4.1.0&q=85", macros: "25g protein" }
            ].map((meal, index) => (
              <div 
                key={meal.name} 
                className={`overflow-hidden animate-fade-in stagger-${index + 1}`} 
                style={{ 
                  background: "#FFFFFF", 
                  border: "1px solid rgba(0,0,0,0.03)", 
                  borderRadius: "20px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  transition: "box-shadow 0.2s, transform 0.2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 14px 40px rgba(0,0,0,0.07)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <img src={meal.img} alt={meal.name} className="w-full h-44 object-cover vora-img" />
                <div className="p-5">
                  <h3 className="text-sm font-semibold mb-2" style={{ color: "#111111" }}>{meal.name}</h3>
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: "rgba(78,122,103,0.07)", color: "#4E7A67" }}>{meal.macros}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 vora-section-a" data-testid="landing-cta-section">
        <div className="container-app text-center">
          <h2 
            className="mb-5" 
            style={{ color: "#111111", fontSize: "clamp(1.5rem, 3vw, 2.25rem)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 }}
            data-testid="landing-cta-heading"
          >
            Ready to let VORA AI optimize your nutrition?
          </h2>
          <p className="text-lg mb-10 max-w-md mx-auto" style={{ color: "#5F6368", lineHeight: 1.6 }}>
            Plans built around your body and goals — free to start.
          </p>
          <Button 
            onClick={() => navigate(forwardParams())}
            data-testid="cta-get-started-btn"
            className="vora-cta-primary rounded-full px-10 py-3.5 text-base"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 vora-section-c" style={{ borderTop: "1px solid rgba(17,17,17,0.05)" }} data-testid="landing-footer">
        <div className="container-app text-center">
          <p className="text-sm" style={{ color: "#8B9096" }}>© 2026 VORA. Eat well, live well.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
